import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Wrapper,
  StandardWrapper,
  LearningJourneyWrapper,
  ExternalKey,
  TopicName,
  Icon,
  IconDropdown,
  Content,
  Info,
  FixedWrapper,
  CollapsedWrapper } from './styled';
import { StandardDropdown, CollapseButton } from '../../helpers/icon-dictionary';
import { getContextCoords } from '../../helpers/utils';
import SvgIcon from '../svg-icon/svg-icon';
import LearningJourney from '../learning-journey';
import ContextModal, { ContextModalWidth } from '../context-modal';
import StandardInfo from '../standard-info';
import {
  displayModalInfo,
  hideStandard
} from '../../containers/upf-assign/upf-assign-actions';
import OutsideClickHandler from '../../helpers/hoc/close/outside-click-handler';
import StandardCollapsed from './collapsed';
import Grid from '../grid';
import CollapsedGrid from '../grid/collapsed';

const Standard = ({ externalKey, topicName, learningJourney, description, id, i18n,
  collapseAllStandards, isStandardDirty, setIsStandardDirty }) => {
  const iconRef = useRef(null);
  const infoRef = useRef(null);
  const dispatch = useDispatch();

  const [showContextMenu, setContextMenu] = useState(false);
  const [showCallOutMenu, setCallOutMenu] = useState(false);
  const [positionX, setPositionX] = useState(0);
  const [positionY, setPositionY] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(collapseAllStandards);
  const [positionArrow, setPositionArrow] = useState(0);
  const [positionXCallout, setPositionXCallout] = useState(0);
  const [positionYCallout, setPositionYCallout] = useState(0);
  const [positionArrowCallout, setPositionArrowCallout] = useState(0);
  const [contextTheme, setContextTheme] = useState(null);

  useEffect(() => {
    if (!isStandardDirty) {
      setIsCollapsed(collapseAllStandards);
    }
  }, [collapseAllStandards, isStandardDirty]);

  const showDropdown = () => {
    const { posX, posY, posArrow } = getContextCoords(iconRef.current.getBoundingClientRect(), ContextModalWidth);
    setPositionY(posY);
    setPositionX(posX);
    setPositionArrow(posArrow);
    setContextTheme(null);
    setCallOutMenu(false);

    if (showContextMenu) {
      setContextMenu(false);
    } else {
      setContextMenu(true);
    }
  };

  const hideStandardBlock = () => {
    dispatch(hideStandard(id));
    dispatch(displayModalInfo(-1));
  };

  const handleExpandCollapse = (state) => {
    setIsStandardDirty(true);
    setIsCollapsed(state);
  };

  const showCallOut = (e) => {
    const { posX, posArrow } = getContextCoords(e, ContextModalWidth);
    const { bottom } = infoRef.current.getBoundingClientRect();
    setPositionYCallout(bottom + 12);
    setPositionXCallout(posX);
    setPositionArrowCallout(posArrow);
    setContextTheme('standard');
    setCallOutMenu(true);
  };

  const hideCallOut = () => {
    setCallOutMenu(false);
  };

  const LearningJourneyIds = learningJourney.length && learningJourney.map(lj => lj.id);

  if (isCollapsed) {
    return (
      <CollapsedWrapper>
        <StandardCollapsed
          externalKey={externalKey}
          handleOnExpand={() => handleExpandCollapse(false)}
          topicName={topicName}
          learningJourney={learningJourney}
          id={id}
          description={description}
          i18n={i18n} />
        <CollapsedGrid
          i18n={i18n}
          learningJourneyIds={LearningJourneyIds} />
      </CollapsedWrapper>
    );
  }

  return (
    <Wrapper>
      <FixedWrapper>
        <StandardWrapper
          lj={learningJourney.length}
          className="standard"
          id="showContextModal">
          <Info
            role="button"
            tabIndex="-1"
            onClick={showDropdown}
            lj={learningJourney.length}
            onMouseEnter={showCallOut}
            onMouseLeave={hideCallOut}
            ref={infoRef}>
            <IconDropdown
              ref={iconRef}>
              <SvgIcon icon={StandardDropdown} />
            </IconDropdown>
            <Content>
              <ExternalKey>{externalKey}</ExternalKey>
              <TopicName>{topicName}</TopicName>
            </Content>
          </Info>
          <Icon
            role="button"
            tabIndex="-1"
            onClick={() => handleExpandCollapse(true)}>
            <SvgIcon icon={CollapseButton} />
          </Icon>
        </StandardWrapper>
        <LearningJourneyWrapper >
          {
            learningJourney.map(lj => (
              <LearningJourney
                id={lj.id}
                key={lj.id}
                ljCode={lj.code}
                ljName={lj.name}
                standardName={topicName}
                externalKey={externalKey}
                description={lj.description}
                i18n={i18n}
                count={lj.childTopics && lj.childTopics.length} />
            ))
          }
        </LearningJourneyWrapper>
      </FixedWrapper>
      <Grid learningJourneyIds={LearningJourneyIds} i18n={i18n} />
      <OutsideClickHandler callback={() => setContextMenu(false)} forButton={`showContextModal_${id}`}>
        <ContextModal
          show={showContextMenu}
          positionArrow={positionArrow}
          positionX={positionX}
          positionY={positionY}>
          <StandardInfo
            standardName={topicName}
            externalKey={externalKey}
            description={description}
            hideStrand={hideStandardBlock}
            displayHideOption
            i18n={i18n} />
        </ContextModal>
        <ContextModal
          show={showCallOutMenu}
          positionArrow={positionArrowCallout}
          positionX={positionXCallout}
          positionY={positionYCallout}
          contextTheme={contextTheme}
          name={topicName} />
      </OutsideClickHandler>
    </Wrapper>
  );
};

Standard.propTypes = {
  externalKey: PropTypes.string,
  topicName: PropTypes.string.isRequired,
  learningJourney: PropTypes.array.isRequired,
  description: PropTypes.string,
  id: PropTypes.number,
  i18n: PropTypes.object,
  collapseAllStandards: PropTypes.bool,
  isStandardDirty: PropTypes.bool.isRequired,
  setIsStandardDirty: PropTypes.func.isRequired
};

Standard.defaultProps = {
  externalKey: '',
  setIsStandardDirty: () => {}
};

Standard.whyDidYouRender = false;

export default Standard;
